import React from 'react';
import { View, Pressable, Platform } from 'react-native';
import { SizableText } from 'tamagui';
import { AntDesign } from '@expo/vector-icons';
import { Text } from '@constants/Themed';

interface ErrorBoundaryProps {
  id?: string;
  children: React.ReactNode;
  onReload?: () => void;
  theme?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.warn(`ErrorBoundary [${this.props.id}] caught an error:`, error);
    this.setState({ hasError: true, error });
    // Optional: Log `this.props.id`, `info`, or `error` to a service
  }

  handleReload = () => {
    // Reset error state to reload the component
    this.setState({ hasError: false, error: null });
    
    // If a custom reload handler is provided, call it
    if (this.props.onReload) {
      this.props.onReload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Safely extract error message
      let errorMessage = 'Unknown error';
      if (this.state.error) {
        if (typeof this.state.error === 'string') {
          errorMessage = this.state.error;
        } else if (this.state.error instanceof Error) {
          errorMessage = this.state.error.message || 'Unknown error';
        } else if (this.state.error?.message) {
          errorMessage = String(this.state.error.message);
        } else {
          errorMessage = String(this.state.error);
        }
      }

      // Check if this is the CSSStyleDeclaration error
      const isCSSStyleError = errorMessage.includes('CSSStyleDeclaration') && 
                              errorMessage.includes('indexed property');
      
      const theme = this.props.theme !== undefined ? this.props.theme : true;
      
      return (
        <View style={{ 
          flex: 1, 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: 20,
          backgroundColor: theme ? '#f9fafb' : '#1f2937'
        }}>
          <View style={{
            backgroundColor: theme ? '#ffffff' : '#374151',
            borderRadius: 12,
            padding: 24,
            maxWidth: '90%',
            alignItems: 'center',
            borderWidth: 1,
            borderColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <AntDesign 
              name="exclamation-circle" 
              size={48} 
              color={theme ? '#ef4444' : '#f87171'} 
              style={{ marginBottom: 16 }}
            />
            <SizableText 
              style={{ 
                fontSize: 18, 
                fontWeight: '700',
                color: theme ? '#1f2937' : '#f9fafb',
                marginBottom: 8,
                textAlign: 'center'
              }}
            >
              {isCSSStyleError ? 'Style Rendering Error' : 'Component Error'}
            </SizableText>
            <SizableText 
              style={{ 
                fontSize: 14,
                color: theme ? '#6b7280' : '#9ca3af',
                marginBottom: 20,
                textAlign: 'center'
              }}
            >
              {isCSSStyleError 
                ? 'A style rendering issue occurred. Reloading the component should fix it.'
                : `${this.props.id ? `[${this.props.id}] ` : ''}Error: ${errorMessage}`
              }
            </SizableText>
            
            {/* Reload Button */}
            <Pressable
              onPress={this.handleReload}
              style={{
                paddingVertical: 12,
                paddingHorizontal: 24,
                backgroundColor: theme ? '#3b82f6' : '#2563eb',
                borderRadius: 8,
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
                gap: 8,
                minWidth: 150,
              }}
            >
              <AntDesign name="reload" size={16} color="white" />
              <Text style={{ color: 'white', fontSize: 16, fontWeight: '600' }}>
                Reload Component
              </Text>
            </Pressable>
          </View>
        </View>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
